import { useEffect, useState, useMemo } from "react";
import {
  Button,
  Table,
  Spin,
  Alert,
  Modal,
  Space,
  message,
} from "antd";
import { ReloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { getAdminDistrictReport, getAdminBlockReport, getAdminGpReport, getBdpoGpReport } from "../api/User";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* =====================================================
   COMPONENT
===================================================== */
const ReportAdmin = ({ dateFilter }) => {
  /* ================= STATE ================= */
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* -------- Block Modal -------- */
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockModalTitle, setBlockModalTitle] = useState("");
  const [blockData, setBlockData] = useState([]);
  const [blockLoading, setBlockLoading] = useState(false);
  const [blockCurrentPage, setBlockCurrentPage] = useState(1);
  const [blockPageSize, setBlockPageSize] = useState(10);

  /* -------- GP Modal -------- */
  const [gpModalOpen, setGpModalOpen] = useState(false);
  const [gpModalTitle, setGpModalTitle] = useState("");
  const [gpData, setGpData] = useState([]);
  const [gpLoading, setGpLoading] = useState(false);
  const [gpCurrentPage, setGpCurrentPage] = useState(1);
  const [gpPageSize, setGpPageSize] = useState(10);

  /* ================= CALCULATE TOTALS ================= */

  // District table totals
  const districtGrandTotals = useMemo(() => {
    if (!data || data.length === 0) return {};

    return data.reduce((acc, item) => ({
      total_block: acc.total_block + (parseInt(item.total_block) || 0),
      total_panchayat: acc.total_panchayat + (parseInt(item.total_panchayat) || 0),
      total_gram_panchayat_gram_sabha_scheduled:
        acc.total_gram_panchayat_gram_sabha_scheduled + (parseInt(item.total_gram_panchayat_gram_sabha_scheduled) || 0),
      total_gram_panchayat_gram_sabha_conducted:
        acc.total_gram_panchayat_gram_sabha_conducted + (parseInt(item.total_gram_panchayat_gram_sabha_conducted) || 0),
      total_panchayat_voter_updated:
        acc.total_panchayat_voter_updated + (parseInt(item.total_panchayat_voter_updated) || 0),
      total_panchayat_attendance_updated:
        acc.total_panchayat_attendance_updated + (parseInt(item.total_panchayat_attendance_updated) || 0),
      number_of_panchayat_image_uploaded:
        acc.number_of_panchayat_image_uploaded + (parseInt(item.number_of_panchayat_image_uploaded) || 0),
      number_of_panchayat_proceeding_book_uploaded:
        acc.number_of_panchayat_proceeding_book_uploaded + (parseInt(item.number_of_panchayat_proceeding_book_uploaded) || 0),
      number_of_panchayat_meeting_video_uploaded:
        acc.number_of_panchayat_meeting_video_uploaded + (parseInt(item.number_of_panchayat_meeting_video_uploaded) || 0),
      income_verified_count:
        acc.income_verified_count + (parseInt(item.income_verified_count) || 0),
      scheme_verified_count:
        acc.scheme_verified_count + (parseInt(item.scheme_verified_count) || 0),
      total_panchayat_with_dev_work:
        acc.total_panchayat_with_dev_work + (parseInt(item.total_panchayat_with_dev_work) || 0),
      total_panchayat_with_amrit_sarovar:
        acc.total_panchayat_with_amrit_sarovar + (parseInt(item.total_panchayat_with_amrit_sarovar) || 0),
    }), {
      total_block: 0,
      total_panchayat: 0,
      total_gram_panchayat_gram_sabha_scheduled: 0,
      total_gram_panchayat_gram_sabha_conducted: 0,
      total_panchayat_voter_updated: 0,
      total_panchayat_attendance_updated: 0,
      number_of_panchayat_image_uploaded: 0,
      number_of_panchayat_proceeding_book_uploaded: 0,
      number_of_panchayat_meeting_video_uploaded: 0,
      income_verified_count: 0,
      scheme_verified_count: 0,
      total_panchayat_with_dev_work: 0,
      total_panchayat_with_amrit_sarovar: 0,
    });
  }, [data]);
  const blockGrandTotals = useMemo(() => {
    if (!blockData || blockData.length === 0) return {};

    return blockData.reduce((acc, item) => ({
      total_gp: acc.total_gp + (parseInt(item.total_gp) || 0),
      gs_scheduled: acc.gs_scheduled + (parseInt(item.gs_scheduled) || 0),
      gs_conducted: acc.gs_conducted + (parseInt(item.gs_conducted) || 0),
      voter_updated: acc.voter_updated + (parseInt(item.voter_updated) || 0),
      attendance_updated: acc.attendance_updated + (parseInt(item.attendance_updated) || 0),
      image_uploaded: acc.image_uploaded + (parseInt(item.image_uploaded) || 0),
      proceeding_book_uploaded: acc.proceeding_book_uploaded + (parseInt(item.proceeding_book_uploaded) || 0),
      meeting_video_uploaded: acc.meeting_video_uploaded + (parseInt(item.meeting_video_uploaded) || 0),
      income_verified: acc.income_verified + (parseInt(item.income_verified) || 0),
      scheme_verified: acc.scheme_verified + (parseInt(item.scheme_verified) || 0),
      development_work_uploaded: acc.development_work_uploaded + (parseInt(item.development_work_uploaded) || 0),
      amrit_sarovar_uploaded: acc.amrit_sarovar_uploaded + (parseInt(item.amrit_sarovar_uploaded) || 0),
    }), {
      total_gp: 0,
      gs_scheduled: 0,
      gs_conducted: 0,
      voter_updated: 0,
      attendance_updated: 0,
      image_uploaded: 0,
      proceeding_book_uploaded: 0,
      meeting_video_uploaded: 0,
      income_verified: 0,
      scheme_verified: 0,
      development_work_uploaded: 0,
      amrit_sarovar_uploaded: 0,
    });
  }, [blockData]);


  // Block modal totals
  const blockPageTotals = useMemo(() => {
    if (!blockData || blockData.length === 0) {
      return {
        total_gp: 0,
        gs_scheduled: 0,
        gs_conducted: 0,
        voter_updated: 0,
        attendance_updated: 0,
        image_uploaded: 0,
        proceeding_book_uploaded: 0,
        meeting_video_uploaded: 0,
        income_verified: 0,
        scheme_verified: 0,
        development_work_uploaded: 0,
        amrit_sarovar_uploaded: 0,
      };
    }

    const startIndex = (blockCurrentPage - 1) * blockPageSize;
    const endIndex = startIndex + blockPageSize;
    const currentPageData = blockData.slice(startIndex, endIndex);

    if (currentPageData.length === 0) {
      return {
        total_gp: 0,
        gs_scheduled: 0,
        gs_conducted: 0,
        voter_updated: 0,
        attendance_updated: 0,
        image_uploaded: 0,
        proceeding_book_uploaded: 0,
        meeting_video_uploaded: 0,
        income_verified: 0,
        scheme_verified: 0,
        development_work_uploaded: 0,
        amrit_sarovar_uploaded: 0,
      };
    }

    return currentPageData.reduce(
      (acc, item) => {
        return {
          total_gp: acc.total_gp + (parseInt(item.total_gp) || 0),
          gs_scheduled: acc.gs_scheduled + (parseInt(item.gs_scheduled) || 0),
          gs_conducted: acc.gs_conducted + (parseInt(item.gs_conducted) || 0),
          voter_updated: acc.voter_updated + (parseInt(item.voter_updated) || 0),
          attendance_updated: acc.attendance_updated + (parseInt(item.attendance_updated) || 0),
          image_uploaded: acc.image_uploaded + (parseInt(item.image_uploaded) || 0),
          proceeding_book_uploaded: acc.proceeding_book_uploaded + (parseInt(item.proceeding_book_uploaded) || 0),
          meeting_video_uploaded: acc.meeting_video_uploaded + (parseInt(item.meeting_video_uploaded) || 0),
          income_verified: acc.income_verified + (parseInt(item.income_verified) || 0),
          scheme_verified: acc.scheme_verified + (parseInt(item.scheme_verified) || 0),
          development_work_uploaded: acc.development_work_uploaded + (parseInt(item.development_work_uploaded) || 0),
          amrit_sarovar_uploaded: acc.amrit_sarovar_uploaded + (parseInt(item.amrit_sarovar_uploaded) || 0),
        };
      },
      {
        total_gp: 0,
        gs_scheduled: 0,
        gs_conducted: 0,
        voter_updated: 0,
        attendance_updated: 0,
        image_uploaded: 0,
        proceeding_book_uploaded: 0,
        meeting_video_uploaded: 0,
        income_verified: 0,
        scheme_verified: 0,
        development_work_uploaded: 0,
        amrit_sarovar_uploaded: 0,
      }
    );
  }, [blockData, blockCurrentPage, blockPageSize]);

  const gpGrandTotals = useMemo(() => {
    if (!gpData || gpData.length === 0) return {};

    return gpData.reduce((acc, item) => ({
      gs_scheduled: acc.gs_scheduled + (parseInt(item.gs_scheduled) || 0),
      gs_conducted: acc.gs_conducted + (parseInt(item.gs_conducted) || 0),
      voter_updated: acc.voter_updated + (parseInt(item.voter_updated) || 0),
      attendance_updated: acc.attendance_updated + (parseInt(item.attendance_updated) || 0),
      image_uploaded: acc.image_uploaded + (parseInt(item.image_uploaded) || 0),
      proceeding_book_uploaded: acc.proceeding_book_uploaded + (parseInt(item.proceeding_book_uploaded) || 0),
      meeting_video_uploaded: acc.meeting_video_uploaded + (parseInt(item.meeting_video_uploaded) || 0),
      income_verified: acc.income_verified + (parseInt(item.income_verified) || 0),
      scheme_verified: acc.scheme_verified + (parseInt(item.scheme_verified) || 0),
      development_work_uploaded: acc.development_work_uploaded + (parseInt(item.development_work_uploaded) || 0),
      amrit_sarovar_uploaded: acc.amrit_sarovar_uploaded + (parseInt(item.amrit_sarovar_uploaded) || 0),
    }), {
      gs_scheduled: 0,
      gs_conducted: 0,
      voter_updated: 0,
      attendance_updated: 0,
      image_uploaded: 0,
      proceeding_book_uploaded: 0,
      meeting_video_uploaded: 0,
      income_verified: 0,
      scheme_verified: 0,
      development_work_uploaded: 0,
      amrit_sarovar_uploaded: 0,
    });
  }, [gpData]);


  // GP modal totals
  const gpPageTotals = useMemo(() => {
    if (!gpData || gpData.length === 0) {
      return {
        gs_scheduled: 0,
        gs_conducted: 0,
        voter_updated: 0,
        attendance_updated: 0,
        image_uploaded: 0,
        proceeding_book_uploaded: 0,
        meeting_video_uploaded: 0,
        income_verified: 0,
        scheme_verified: 0,
        development_work_uploaded: 0,
        amrit_sarovar_uploaded: 0,
      };
    }

    const startIndex = (gpCurrentPage - 1) * gpPageSize;
    const endIndex = startIndex + gpPageSize;
    const currentPageData = gpData.slice(startIndex, endIndex);

    if (currentPageData.length === 0) {
      return {
        gs_scheduled: 0,
        gs_conducted: 0,
        voter_updated: 0,
        attendance_updated: 0,
        image_uploaded: 0,
        proceeding_book_uploaded: 0,
        meeting_video_uploaded: 0,
        income_verified: 0,
        scheme_verified: 0,
        development_work_uploaded: 0,
        amrit_sarovar_uploaded: 0,
      };
    }

    return currentPageData.reduce(
      (acc, item) => {
        return {
          gs_scheduled: acc.gs_scheduled + (parseInt(item.gs_scheduled) || 0),
          gs_conducted: acc.gs_conducted + (parseInt(item.gs_conducted) || 0),
          voter_updated: acc.voter_updated + (parseInt(item.voter_updated) || 0),
          attendance_updated: acc.attendance_updated + (parseInt(item.attendance_updated) || 0),
          image_uploaded: acc.image_uploaded + (parseInt(item.image_uploaded) || 0),
          proceeding_book_uploaded: acc.proceeding_book_uploaded + (parseInt(item.proceeding_book_uploaded) || 0),
          meeting_video_uploaded: acc.meeting_video_uploaded + (parseInt(item.meeting_video_uploaded) || 0),
          income_verified: acc.income_verified + (parseInt(item.income_verified) || 0),
          scheme_verified: acc.scheme_verified + (parseInt(item.scheme_verified) || 0),
          development_work_uploaded: acc.development_work_uploaded + (parseInt(item.development_work_uploaded) || 0),
          amrit_sarovar_uploaded: acc.amrit_sarovar_uploaded + (parseInt(item.amrit_sarovar_uploaded) || 0),
        };
      },
      {
        gs_scheduled: 0,
        gs_conducted: 0,
        voter_updated: 0,
        attendance_updated: 0,
        image_uploaded: 0,
        proceeding_book_uploaded: 0,
        meeting_video_uploaded: 0,
        income_verified: 0,
        scheme_verified: 0,
        development_work_uploaded: 0,
        amrit_sarovar_uploaded: 0,
      }
    );
  }, [gpData, gpCurrentPage, gpPageSize]);

  /* ================= MAIN TABLE COLUMNS ================= */
  const columns = [
    {
      title: "District Name",
      dataIndex: "district_name",
      width: 160,
      align: "center",
    },
    {
      title: "Block Count",
      dataIndex: "total_block",
      width: 120,
      align: "center",
      render: (value, record) => (
        <span onClick={() => openBlockModal(record)} style={clickStyle}>
          {value}
        </span>
      ),
    },
    {
      title: "Total Gram Panchayat",
      dataIndex: "total_panchayat",
      width: 160,
      align: "center",
      render: (value, record) => (
        <span onClick={() => openGPFromDistrict(record)} style={clickStyle}>
          {value}
        </span>
      ),
    },
    {
      title: "Total Gram Panchayat where Gram Sabha Scheduled",
      dataIndex: "total_gram_panchayat_gram_sabha_scheduled",
      width: 300,
      align: "center",
    },
    {
      title: "Total Gram Panchayat where Gram Sabha Conducted",
      dataIndex: "total_gram_panchayat_gram_sabha_conducted",
      width: 300,
      align: "center",
    },
    {
      title: "No. of Panchayat where Voter Number Updated",
      dataIndex: "total_panchayat_voter_updated",
      width: 300,
      align: "center",
    },
    {
      title: "No. of Panchayat where Attendance Updated",
      dataIndex: "total_panchayat_attendance_updated",
      width: 300,
      align: "center",
    },
    {
      title: "No. of Panchayat where Image Uploaded",
      dataIndex: "number_of_panchayat_image_uploaded",
      width: 300,
      align: "center",
    },
    {
      title: "No. of Panchayat where Proceeding Book Uploaded",
      dataIndex: "number_of_panchayat_proceeding_book_uploaded",
      width: 340,
      align: "center",
    },
    {
      title: "No. of Panchayat where Meeting Video Uploaded",
      dataIndex: "number_of_panchayat_meeting_video_uploaded",
      width: 340,
      align: "center",
    },
    ...(dateFilter !== "after" ? [
      {
        title: "Income Verified",
        dataIndex: "income_verified_count",
        width: 160,
        align: "center",
      },
      {
        title: "Scheme Verified",
        dataIndex: "scheme_verified_count",
        width: 160,
        align: "center",
      }
    ] : []),
    {
      title: "No. of Panchayat where Development Work Uploaded",
      dataIndex: "total_panchayat_with_dev_work",
      width: 360,
      align: "center",
    },
    {
      title: "No.of Panchayat where Detail of Amrit Sarovar Uploaded",
      dataIndex: "total_panchayat_with_amrit_sarovar",
      width: 360,
      align: "center",
    },
  ];


  /* ================= BLOCK MODAL COLUMNS ================= */
  const blockColumns = [
    { title: "District Name", dataIndex: "district_name", width: 180, align: "center", fixed: "left" },
    { title: "Block Name", dataIndex: "block_name", width: 200, align: "center", fixed: "left" },
    {
      title: "Total Gram Panchayat",
      dataIndex: "total_gp",
      width: 120,
      align: "center",
      render: (value, record) => (
        <span
          onClick={() => openGPFromBlock(record)}
          style={clickStyle}
        >
          {value}
        </span>
      ),
    },
    { title: "Total Gram Panchayat where Gram Sabha Scheduled", dataIndex: "gs_scheduled", width: 120, align: "center" },
    { title: "Total Gram Panchayat where Gram Sabha Conducted", dataIndex: "gs_conducted", width: 120, align: "center" },
    { title: "No. of Panchayat where Voter Number Updated", dataIndex: "voter_updated", width: 120, align: "center" },
    { title: "No. of Panchayat where Attendance Updated", dataIndex: "attendance_updated", width: 140, align: "center" },
    { title: "No. of Panchayat where Image Uploaded", dataIndex: "image_uploaded", width: 130, align: "center" },
    { title: "No. of Panchayat where Proceeding Book Uploaded", dataIndex: "proceeding_book_uploaded", width: 150, align: "center" },
    { title: "No. of Panchayat where Meeting Video Uploaded", dataIndex: "meeting_video_uploaded", width: 150, align: "center" },
    ...(dateFilter !== "after" ? [
      { title: "Income Verified", dataIndex: "income_verified", width: 120, align: "center" },
      { title: "Scheme Verified", dataIndex: "scheme_verified", width: 120, align: "center" }
    ] : []),
    { title: "No. of Panchayat where Development Work Uploaded", dataIndex: "development_work_uploaded", width: 160, align: "center" },
    { title: "No.of Panchayat where Detail of Amrit Sarovar Uploaded", dataIndex: "amrit_sarovar_uploaded", width: 150, align: "center" },
  ];

  /* ================= GP MODAL COLUMNS ================= */
  const gpColumns = [
    { title: "District Name", dataIndex: "district_name", width: 180, align: "center", fixed: "left" },
    { title: "Block Name", dataIndex: "block_name", width: 180, align: "center", fixed: "left" },
    { title: "Gram Panchayat Name", dataIndex: "gp_name", width: 240, align: "center", fixed: "left" },
    { title: "Total Gram Panchayat where Gram Sabha Scheduled", dataIndex: "gs_scheduled", width: 120, align: "center" },
    { title: "Total Gram Panchayat where Gram Sabha Conducted", dataIndex: "gs_conducted", width: 120, align: "center" },
    { title: "No. of Panchayat where Voter Number Updated", dataIndex: "voter_updated", width: 120, align: "center" },
    { title: "No. of Panchayat where Attendance Updated", dataIndex: "attendance_updated", width: 140, align: "center" },
    { title: "No. of Panchayat where Image Uploaded", dataIndex: "image_uploaded", width: 130, align: "center" },
    { title: "No. of Panchayat where Proceeding Book Uploaded", dataIndex: "proceeding_book_uploaded", width: 150, align: "center" },
    { title: "No. of Panchayat where Meeting Video Uploaded", dataIndex: "meeting_video_uploaded", width: 150, align: "center" },
    ...(dateFilter !== "after" ? [
      { title: "Income Verified", dataIndex: "income_verified", width: 120, align: "center" },
      { title: "Scheme Verified", dataIndex: "scheme_verified", width: 120, align: "center" }
    ] : []),
    { title: "No. of Panchayat where Development Work Uploaded", dataIndex: "development_work_uploaded", width: 160, align: "center" },
    { title: "No.of Panchayat where Detail of Amrit Sarovar Uploaded", dataIndex: "amrit_sarovar_uploaded", width: 150, align: "center" },
  ];

  /* ================= FETCH DISTRICT DATA ================= */
  useEffect(() => {
    fetchDistrictData();
  }, [dateFilter]);

  const fetchDistrictData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem("token");

      if (!token) {
        setError("No authentication token found. Please login again.");
        setLoading(false);
        return;
      }

      console.log("Fetching admin district data...");
      const result = await getAdminDistrictReport(token, dateFilter);

      if (result.status === 'success') {
        setData(result.data.map((item, index) => ({
          key: index + 1,
          district_name: item.district_name,
          total_block: item.total_block,
          total_panchayat: item.total_panchayat,
          total_gram_panchayat_gram_sabha_scheduled: item.total_gram_panchayat_gram_sabha_scheduled,
          total_gram_panchayat_gram_sabha_conducted: item.total_gram_panchayat_gram_sabha_conducted,
          total_panchayat_voter_updated: item.total_panchayat_voter_updated,
          total_panchayat_attendance_updated: item.total_panchayat_attendance_updated,
          number_of_panchayat_image_uploaded: item.number_of_panchayat_image_uploaded,
          number_of_panchayat_proceeding_book_uploaded: item.number_of_panchayat_proceeding_book_uploaded,
          number_of_panchayat_meeting_video_uploaded: item.number_of_panchayat_meeting_video_uploaded,
          income_verified_count: item.income_verified_count,
          scheme_verified_count: item.scheme_verified_count,
          total_panchayat_with_dev_work: item.total_panchayat_with_dev_work,
          total_panchayat_with_amrit_sarovar: item.total_panchayat_with_amrit_sarovar,
          district_code: item.district_code, // Store district_code for drill-down
        })));
      } else {
        setError(result.message || 'Failed to fetch district data');
      }
    } catch (err) {
      console.error('Error fetching district data:', err);
      setError('Failed to fetch district data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ================= DRILL FUNCTIONS ================= */
  const openBlockModal = async (district) => {
    console.log('Opening block modal for district:', district.district_name, 'District Code:', district.district_code);
    setBlockModalTitle(`Block-wise Report : ${district.district_name}`);
    setBlockModalOpen(true);
    setBlockData([]); // Clear previous data
    setBlockLoading(true); // Start loading
    setBlockCurrentPage(1); // Reset to first page

    try {
      const token = sessionStorage.getItem("token");

      if (!token) {
        console.error('No token found in sessionStorage');
        setError("No authentication token found. Please login again.");
        setBlockLoading(false);
        return;
      }

      console.log('Calling getAdminBlockReport API with district_code:', district.district_code);
      const result = await getAdminBlockReport(token, district.district_code, dateFilter);
      console.log('Block API Response:', result);

      if (result.status === 'success') {
        console.log('Block Data received:', result.data.length, 'records');
        setBlockData(result.data.map((item, index) => ({
          key: index + 1,
          district_name: item.district_name,
          block_name: item.block_name,
          total_gp: item.total_gp,
          gs_scheduled: item.gs_scheduled,
          gs_conducted: item.gs_conducted,
          voter_updated: item.voter_updated,
          attendance_updated: item.attendance_updated,
          image_uploaded: item.image_uploaded,
          proceeding_book_uploaded: item.proceeding_book_uploaded,
          meeting_video_uploaded: item.meeting_video_uploaded,
          income_verified: item.income_verified,
          scheme_verified: item.scheme_verified,
          development_work_uploaded: item.development_work_uploaded,
          amrit_sarovar_uploaded: item.amrit_sarovar_uploaded,
          block_code: item.block_code,
        })));
      } else {
        console.error('Failed to fetch block data:', result.message);
        setError(`Failed to fetch block data: ${result.message}`);
        setBlockData([]);
      }
    } catch (err) {
      console.error('Error fetching block data:', err);
      setError('Failed to fetch block data. Please try again.');
      setBlockData([]);
    } finally {
      setBlockLoading(false); // Stop loading
    }
  };

  const openGPFromDistrict = async (district) => {
    console.log('Opening GP modal for district:', district.district_name, 'District Code:', district.district_code);
    setGpModalTitle(`Gram Panchayat-wise Report : ${district.district_name}`);
    setGpModalOpen(true);
    setGpData([]); // Clear previous data
    setGpLoading(true); // Start loading
    setGpCurrentPage(1); // Reset to first page

    try {
      const token = sessionStorage.getItem("token");

      if (!token) {
        console.error('No token found in sessionStorage');
        setError("No authentication token found. Please login again.");
        setGpLoading(false);
        return;
      }

      console.log('Calling getAdminGpReport API with district_code:', district.district_code);
      const result = await getAdminGpReport(token, district.district_code, dateFilter);
      console.log('GP API Response:', result);

      if (result.status === 'success') {
        console.log('GP Data received:', result.data.length, 'records');
        setGpData(result.data.map((item, index) => ({
          key: index + 1,
          district_name: district.district_name,
          block_name: item.block_name,
          gp_name: item.gp_name || item.panchayat_name,
          gs_scheduled: item.gs_scheduled || 0,
          gs_conducted: item.gs_conducted || 0,
          voter_updated: item.voter_updated || 0,
          attendance_updated: item.attendance_updated || 0,
          image_uploaded: item.image_uploaded || 0,
          proceeding_book_uploaded: item.proceeding_book_uploaded || 0,
          meeting_video_uploaded: item.meeting_video_uploaded || 0,
          income_verified: item.income_verified || 0,
          scheme_verified: item.scheme_verified || 0,
          development_work_uploaded: item.development_work_uploaded || 0,
          amrit_sarovar_uploaded: item.amrit_sarovar_uploaded || 0,
        })));
      } else {
        console.error('Failed to fetch GP data:', result.message);
        setError(`Failed to fetch GP data: ${result.message}`);
        setGpData([]);
      }
    } catch (err) {
      console.error('Error fetching GP data:', err);
      setError('Failed to fetch GP data. Please try again.');
      setGpData([]);
    } finally {
      setGpLoading(false); // Stop loading
    }
  };

  const openGPFromBlock = async (block) => {
    setBlockModalOpen(false);
    console.log('Opening GP modal for block:', block.block_name, 'Block Code:', block.block_code);
    setGpModalTitle(`Gram Panchayat-wise Report : ${block.block_name}`);
    setGpModalOpen(true);
    setGpData([]); // Clear previous data
    setGpLoading(true); // Start loading
    setGpCurrentPage(1); // Reset to first page

    try {
      const token = sessionStorage.getItem("token");

      if (!token) {
        console.error('No token found in sessionStorage');
        setError("No authentication token found. Please login again.");
        setGpLoading(false);
        return;
      }

      console.log('Calling getBdpoGpReport API with block_code:', block.block_code);
      const result = await getBdpoGpReport(token, block.block_code);
      console.log('GP API Response:', result);

      if (result.status === 'success') {
        console.log('GP Data received:', result.data.length, 'records');
        setGpData(result.data.map((item, index) => ({
          key: index + 1,
          district_name: block.district_name,
          block_name: block.block_name,
          gp_name: item.gp_name || item.panchayat_name,
          gs_scheduled: item.gs_scheduled || 0,
          gs_conducted: item.gs_conducted || 0,
          voter_updated: item.voter_updated || 0,
          attendance_updated: item.attendance_updated || 0,
          image_uploaded: item.image_uploaded || 0,
          proceeding_book_uploaded: item.proceeding_book_uploaded || 0,
          meeting_video_uploaded: item.meeting_video_uploaded || 0,
          income_verified: item.income_verified || 0,
          scheme_verified: item.scheme_verified || 0,
          development_work_uploaded: item.development_work_uploaded || 0,
          amrit_sarovar_uploaded: item.amrit_sarovar_uploaded || 0,
        })));
      } else {
        console.error('Failed to fetch GP data:', result.message);
        setError(`Failed to fetch GP data: ${result.message}`);
        setGpData([]);
      }
    } catch (err) {
      console.error('Error fetching GP data:', err);
      setError('Failed to fetch GP data. Please try again.');
      setGpData([]);
    } finally {
      setGpLoading(false); // Stop loading
    }
  };

  /* ================= EXPORT FUNCTIONS ================= */

  const handleExportDistrictPDF = () => {
    if (!data.length) return message.warning("No data to export");

    const doc = new jsPDF("landscape");

    doc.setFontSize(14);
    doc.text("Admin District Special Gram Sabha Report", 14, 15);

    const tableColumns = [
      "District",
      "Blocks",
      "GPs",
      "GS Scheduled",
      "GS Conducted",
      "Voter Updated",
      "Attendance Updated",
      "Images",
      "Proceeding Book",
      "Meeting Video",
      "Income Verified",
      "Scheme Verified",
      "Dev Work",
      "Amrit Sarovar",
    ];

    const tableRows = data.map(row => [
      row.district_name,
      row.total_block,
      row.total_panchayat,
      row.total_gram_panchayat_gram_sabha_scheduled,
      row.total_gram_panchayat_gram_sabha_conducted,
      row.total_panchayat_voter_updated,
      row.total_panchayat_attendance_updated,
      row.number_of_panchayat_image_uploaded,
      row.number_of_panchayat_proceeding_book_uploaded,
      row.number_of_panchayat_meeting_video_uploaded,
      row.income_verified_count,
      row.scheme_verified_count,
      row.total_panchayat_with_dev_work,
      row.total_panchayat_with_amrit_sarovar,
    ]);

    // ✅ GRAND TOTAL ROW
    tableRows.push([
      "GRAND TOTAL",
      districtGrandTotals.total_block,
      districtGrandTotals.total_panchayat,
      districtGrandTotals.total_gram_panchayat_gram_sabha_scheduled,
      districtGrandTotals.total_gram_panchayat_gram_sabha_conducted,
      districtGrandTotals.total_panchayat_voter_updated,
      districtGrandTotals.total_panchayat_attendance_updated,
      districtGrandTotals.number_of_panchayat_image_uploaded,
      districtGrandTotals.number_of_panchayat_proceeding_book_uploaded,
      districtGrandTotals.number_of_panchayat_meeting_video_uploaded,
      districtGrandTotals.income_verified_count,
      districtGrandTotals.scheme_verified_count,
      districtGrandTotals.total_panchayat_with_dev_work,
      districtGrandTotals.total_panchayat_with_amrit_sarovar,
    ]);

    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 22,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [22, 119, 255] },
      didParseCell: (data) => {
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fontStyle = "bold";
        }
      }
    });

    doc.save("Admin_District_Report.pdf");
  };

  // Export main district table to Excel
  const handleExportDistrictExcel = () => {
    if (!data || data.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      const excelData = data.map((row, idx) => ({
        "Sr. No.": idx + 1,
        "District Name": row.district_name || "-",
        "Block Count": row.total_block || 0,
        "Total Gram Panchayat": row.total_panchayat || 0,
        "Total Gram Panchayat where Gram Sabha Scheduled": row.total_gram_panchayat_gram_sabha_scheduled || 0,
        "Total Gram Panchayat where Gram Sabha Conducted": row.total_gram_panchayat_gram_sabha_conducted || 0,
        "No. of Panchayat where Voter Number Updated": row.total_panchayat_voter_updated || 0,
        "No. of Panchayat where Attendance Updated": row.total_panchayat_attendance_updated || 0,
        "No. of Panchayat where Image Uploaded": row.number_of_panchayat_image_uploaded || 0,
        "No. of Panchayat where Proceeding Book Uploaded": row.number_of_panchayat_proceeding_book_uploaded || 0,
        "No. of Panchayat where Meeting Video Uploaded": row.number_of_panchayat_meeting_video_uploaded || 0,
        "Income Verified ": row.income_verified_count || 0,
        "Scheme Verified ": row.scheme_verified_count || 0,
        "Number of Panchayat where Development Work Uploaded": row.total_panchayat_with_dev_work || 0,
        "Number of Panchayat where Detail of Amrit Sarovar Uploaded": row.total_panchayat_with_amrit_sarovar || 0,
      }));

      // 👉 ADD THIS LINE HERE
      excelData.push({
        "Sr. No.": "GRAND TOTAL",
        "District Name": "",
        "Block Count": districtGrandTotals.total_block,
        "Total Gram Panchayat": districtGrandTotals.total_panchayat,
        "Total Gram Panchayat where Gram Sabha Scheduled":
          districtGrandTotals.total_gram_panchayat_gram_sabha_scheduled,
        "Total Gram Panchayat where Gram Sabha Conducted":
          districtGrandTotals.total_gram_panchayat_gram_sabha_conducted,
        "No. of Panchayat where Voter Number Updated":
          districtGrandTotals.total_panchayat_voter_updated,
        "No. of Panchayat where Attendance Updated":
          districtGrandTotals.total_panchayat_attendance_updated,
        "No. of Panchayat where Image Uploaded":
          districtGrandTotals.number_of_panchayat_image_uploaded,
        "No. of Panchayat where Proceeding Book Uploaded":
          districtGrandTotals.number_of_panchayat_proceeding_book_uploaded,
        "No. of Panchayat where Meeting Video Uploaded":
          districtGrandTotals.number_of_panchayat_meeting_video_uploaded,
        "Income Verified ": districtGrandTotals.income_verified_count,
        "Scheme Verified ": districtGrandTotals.scheme_verified_count,
        "Number of Panchayat where Development Work Uploaded":
          districtGrandTotals.total_panchayat_with_dev_work,
        "Number of Panchayat where Detail of Amrit Sarovar Uploaded":
          districtGrandTotals.total_panchayat_with_amrit_sarovar,
      });


      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "District Report");

      const fileName = `Admin_District_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      message.success("Excel file exported successfully");
    } catch (err) {
      console.error("Export Excel error:", err);
      message.error("Failed to export Excel file");
    }
  };

  // Export block modal data to Excel
  const handleExportBlockExcel = () => {
    if (!blockData || blockData.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      const excelData = blockData.map((row, idx) => ({
        "Sr. No.": idx + 1,
        "District Name": row.district_name || "-",
        "Block Name": row.block_name || "-",
        "Total Gram Panchayat": row.total_gp || 0,
        "Total Gram Panchayat where Gram Sabha Scheduled": row.gs_scheduled || 0,
        "Total Gram Panchayat where Gram Sabha Conducted": row.gs_conducted || 0,
        "No. of Panchayat where Voter Number Updated": row.voter_updated || 0,
        "No. of Panchayat where Attendance Updated": row.attendance_updated || 0,
        "No. of Panchayat where Image Uploaded": row.image_uploaded || 0,
        "No. of Panchayat where Proceeding Book Uploaded": row.proceeding_book_uploaded || 0,
        "No. of Panchayat where Meeting Video Uploaded": row.meeting_video_uploaded || 0,
        "Income Verified": row.income_verified || 0,
        "Scheme Verified": row.scheme_verified || 0,
        "No. of Panchayat where Development Work Uploaded": row.development_work_uploaded || 0,
        "No.of Panchayat where Detail of Amrit Sarovar Uploaded": row.amrit_sarovar_uploaded || 0,
      }));
      excelData.push({
        "Sr. No.": "GRAND TOTAL",
        "District Name": "",
        "Block Name": "",
        "Total Gram Panchayat": blockGrandTotals.total_gp,
        "Total Gram Panchayat where Gram Sabha Scheduled": blockGrandTotals.gs_scheduled,
        "Total Gram Panchayat where Gram Sabha Conducted": blockGrandTotals.gs_conducted,
        "No. of Panchayat where Voter Number Updated": blockGrandTotals.voter_updated,
        "No. of Panchayat where Attendance Updated": blockGrandTotals.attendance_updated,
        "No. of Panchayat where Image Uploaded": blockGrandTotals.image_uploaded,
        "No. of Panchayat where Proceeding Book Uploaded": blockGrandTotals.proceeding_book_uploaded,
        "No. of Panchayat where Meeting Video Uploaded": blockGrandTotals.meeting_video_uploaded,
        "Income Verified": blockGrandTotals.income_verified,
        "Scheme Verified": blockGrandTotals.scheme_verified,
        "No. of Panchayat where Development Work Uploaded": blockGrandTotals.development_work_uploaded,
        "No.of Panchayat where Detail of Amrit Sarovar Uploaded": blockGrandTotals.amrit_sarovar_uploaded,
      });


      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Block Report");

      const fileName = `Admin_Block_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      message.success("Excel file exported successfully");
    } catch (err) {
      console.error("Export Excel error:", err);
      message.error("Failed to export Excel file");
    }
  };

  // Export GP modal data to Excel
  const handleExportGpExcel = () => {
    if (!gpData || gpData.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      const excelData = gpData.map((row, idx) => ({
        "Sr. No.": idx + 1,
        "District Name": row.district_name || "-",
        "Block Name": row.block_name || "-",
        "GP Name": row.gp_name || "-",
        "Total Gram Panchayat where Gram Sabha Scheduled": row.gs_scheduled || 0,
        "Total Gram Panchayat where Gram Sabha Conducted": row.gs_conducted || 0,
        "No. of Panchayat where Voter Number Updated": row.voter_updated || 0,
        "No. of Panchayat where Attendance Updated": row.attendance_updated || 0,
        "No. of Panchayat where Image Uploaded": row.image_uploaded || 0,
        "No. of Panchayat where Proceeding Book Uploaded": row.proceeding_book_uploaded || 0,
        "No. of Panchayat where Meeting Video Uploaded": row.meeting_video_uploaded || 0,
        "Income Verified": row.income_verified || 0,
        "Scheme Verified": row.scheme_verified || 0,
        "No. of Panchayat where Development Work Uploaded": row.development_work_uploaded || 0,
        "No.of Panchayat where Detail of Amrit Sarovar Uploaded": row.amrit_sarovar_uploaded || 0,
      }));
      excelData.push({
        "Sr. No.": "GRAND TOTAL",
        "District Name": "",
        "Block Name": "",
        "GP Name": "",
        "Total Gram Panchayat where Gram Sabha Scheduled": gpGrandTotals.gs_scheduled,
        "Total Gram Panchayat where Gram Sabha Conducted": gpGrandTotals.gs_conducted,
        "No. of Panchayat where Voter Number Updated": gpGrandTotals.voter_updated,
        "No. of Panchayat where Attendance Updated": gpGrandTotals.attendance_updated,
        "No. of Panchayat where Image Uploaded": gpGrandTotals.image_uploaded,
        "No. of Panchayat where Proceeding Book Uploaded": gpGrandTotals.proceeding_book_uploaded,
        "No. of Panchayat where Meeting Video Uploaded": gpGrandTotals.meeting_video_uploaded,
        "Income Verified": gpGrandTotals.income_verified,
        "Scheme Verified": gpGrandTotals.scheme_verified,
        "No. of Panchayat where Development Work Uploaded": gpGrandTotals.development_work_uploaded,
        "No.of Panchayat where Detail of Amrit Sarovar Uploaded": gpGrandTotals.amrit_sarovar_uploaded,
      });


      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "GP Report");

      const fileName = `Admin_GP_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      message.success("Excel file exported successfully");
    } catch (err) {
      console.error("Export Excel error:", err);
      message.error("Failed to export Excel file");
    }
  };

  /* ================= UI ================= */
  return (
    <div className="report-container">
      <div className="report-card">
        <div className="report-header">
          <div>
            <h2 className="report-title">Detailed report of Special Gram Sabha (Admin)</h2>

          </div>

          <Space>
            <Button onClick={handleExportDistrictPDF}
            >
              Export PDF
            </Button>

            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportDistrictExcel}
              disabled={loading || data.length === 0}
            >
              Export Excel
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchDistrictData}>
              Refresh
            </Button>
          </Space>
        </div>

        {error && <Alert type="error" message={error} />}

        {loading ? (
          <Spin />
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={data}
              rowKey="key"
              bordered
              size="small"
              scroll={{ x: 3000, y: 420 }}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                pageSizeOptions: ["10", "20", "50", "100"],
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} districts`,
                total: data.length,
              }}
              onChange={(pagination) => {
                setCurrentPage(pagination.current);
                setPageSize(pagination.pageSize);
              }}
            />

            {/* Total Row for District Table */}
            {data.length > 0 && (
              <div style={{
                backgroundColor: "#e6f7ff",
                border: "1px solid #91d5ff",
                borderRadius: "4px",
                padding: "8px 12px",
                marginTop: "8px",
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                fontSize: "11px",
              }}>
                <span><strong>Grand Totals (All Pages):</strong></span>

                <span>Blocks: <strong>{districtGrandTotals.total_block.toLocaleString()}</strong></span>
                <span>GPs: <strong>{districtGrandTotals.total_panchayat.toLocaleString()}</strong></span>
                <span>GS Scheduled: <strong>{districtGrandTotals.total_gram_panchayat_gram_sabha_scheduled.toLocaleString()}</strong></span>
                <span>GS Conducted: <strong>{districtGrandTotals.total_gram_panchayat_gram_sabha_conducted.toLocaleString()}</strong></span>
                <span>Voter Updated: <strong>{districtGrandTotals.total_panchayat_voter_updated.toLocaleString()}</strong></span>
                <span>Attendance Updated: <strong>{districtGrandTotals.total_panchayat_attendance_updated.toLocaleString()}</strong></span>
                <span>Image Uploaded: <strong>{districtGrandTotals.number_of_panchayat_image_uploaded.toLocaleString()}</strong></span>
                <span>Proceeding Book: <strong>{districtGrandTotals.number_of_panchayat_proceeding_book_uploaded.toLocaleString()}</strong></span>
                <span>Meeting Video: <strong>{districtGrandTotals.number_of_panchayat_meeting_video_uploaded.toLocaleString()}</strong></span>
                {dateFilter !== "after" && (
                  <>
                    <span>Income Verified: <strong>{districtGrandTotals.income_verified_count.toLocaleString()}</strong></span>
                    <span>Scheme Verified: <strong>{districtGrandTotals.scheme_verified_count.toLocaleString()}</strong></span>
                  </>
                )}
                <span>Dev Work: <strong>{districtGrandTotals.total_panchayat_with_dev_work.toLocaleString()}</strong></span>
                <span>Amrit Sarovar: <strong>{districtGrandTotals.total_panchayat_with_amrit_sarovar.toLocaleString()}</strong></span>
              </div>
            )}
          </>
        )}
      </div>

      {/* ========== BLOCK MODAL ========== */}
      <Modal
        open={blockModalOpen}
        onCancel={() => setBlockModalOpen(false)}
        width={1200}
        footer={null}
        closable={true}
        title={
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingRight: "40px"

          }}>
            <span style={{ fontWeight: 600 }}>{blockModalTitle}</span>

            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportBlockExcel}
              disabled={blockLoading || blockData.length === 0}
            >
              Export Excel
            </Button>
          </div>
        }
      >
        {blockLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <Table
              columns={blockColumns}
              dataSource={blockData}
              rowKey="key"
              bordered
              size="small"
              scroll={{ x: 1800, y: 400 }}
              pagination={{
                current: blockCurrentPage,
                pageSize: blockPageSize,
                pageSizeOptions: ["10", "20", "50", "100"],
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} blocks`,
                total: blockData.length,
              }}
              onChange={(pagination) => {
                setBlockCurrentPage(pagination.current);
                setBlockPageSize(pagination.pageSize);
              }}
            />

            {/* Total Row for Block Modal */}
            {blockData.length > 0 && (
              <div style={{
                backgroundColor: "#e6f7ff",
                border: "1px solid #91d5ff",
                borderRadius: "4px",
                padding: "8px 12px",
                marginTop: "8px",
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                fontSize: "11px",
              }}>
                <span><strong>Grand Totals:</strong></span>
                <span>Total GP: <strong>{blockGrandTotals.total_gp.toLocaleString()}</strong></span>
                <span>GS Scheduled: <strong>{blockGrandTotals.gs_scheduled.toLocaleString()}</strong></span>
                <span>GS Conducted: <strong>{blockGrandTotals.gs_conducted.toLocaleString()}</strong></span>
                <span>Voter Updated: <strong>{blockGrandTotals.voter_updated.toLocaleString()}</strong></span>
                <span>Attendance Updated: <strong>{blockGrandTotals.attendance_updated.toLocaleString()}</strong></span>
                <span>Image Uploaded: <strong>{blockGrandTotals.image_uploaded.toLocaleString()}</strong></span>
                <span>Proceeding Book: <strong>{blockGrandTotals.proceeding_book_uploaded.toLocaleString()}</strong></span>
                <span>Meeting Video: <strong>{blockGrandTotals.meeting_video_uploaded.toLocaleString()}</strong></span>
                {dateFilter !== "after" && (
                  <>
                    <span>Income Verified: <strong>{blockGrandTotals.income_verified.toLocaleString()}</strong></span>
                    <span>Scheme Verified: <strong>{blockGrandTotals.scheme_verified.toLocaleString()}</strong></span>
                  </>
                )}
                <span>Dev Work: <strong>{blockGrandTotals.development_work_uploaded.toLocaleString()}</strong></span>
                <span>Amrit Sarovar: <strong>{blockGrandTotals.amrit_sarovar_uploaded.toLocaleString()}</strong></span>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* ========== GP MODAL ========== */}
      <Modal
        open={gpModalOpen}
        onCancel={() => setGpModalOpen(false)}
        width={1200}
        footer={null}
        closable={true}
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingRight: "40px" // ⭐ gap from close icon
            }}
          >
            <span style={{ fontWeight: 600 }}>{gpModalTitle}</span>

            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportGpExcel}
              disabled={gpLoading || gpData.length === 0}
            >
              Export Excel
            </Button>
          </div>
        }
      >
        {gpLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <Table
              columns={gpColumns}
              dataSource={gpData}
              rowKey="key"
              bordered
              size="small"
              scroll={{ x: 1600, y: 400 }}
              pagination={{
                current: gpCurrentPage,
                pageSize: gpPageSize,
                pageSizeOptions: ["10", "20", "50", "100"],
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} GPs`,
                total: gpData.length,
              }}
              onChange={(pagination) => {
                setGpCurrentPage(pagination.current);
                setGpPageSize(pagination.pageSize);
              }}
            />

            {/* Total Row for GP Modal */}
            {gpData.length > 0 && (
              <div style={{
                backgroundColor: "#e6f7ff",
                border: "1px solid #91d5ff",
                borderRadius: "4px",
                padding: "8px 12px",
                marginTop: "8px",
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                fontSize: "11px",
              }}>
                <span><strong>Grand Totals:</strong></span>
                <span>GS Scheduled: <strong>{gpGrandTotals.gs_scheduled.toLocaleString()}</strong></span>
                <span>GS Conducted: <strong>{gpGrandTotals.gs_conducted.toLocaleString()}</strong></span>
                <span>Voter Updated: <strong>{gpGrandTotals.voter_updated.toLocaleString()}</strong></span>
                <span>Attendance Updated: <strong>{gpGrandTotals.attendance_updated.toLocaleString()}</strong></span>
                <span>Image Uploaded: <strong>{gpGrandTotals.image_uploaded.toLocaleString()}</strong></span>
                <span>Proceeding Book: <strong>{gpGrandTotals.proceeding_book_uploaded.toLocaleString()}</strong></span>
                <span>Meeting Video: <strong>{gpGrandTotals.meeting_video_uploaded.toLocaleString()}</strong></span>
                {dateFilter !== "after" && (
                  <>
                    <span>Income Verified: <strong>{gpGrandTotals.income_verified.toLocaleString()}</strong></span>
                    <span>Scheme Verified: <strong>{gpGrandTotals.scheme_verified.toLocaleString()}</strong></span>
                  </>
                )}
                <span>Dev Work: <strong>{gpGrandTotals.development_work_uploaded.toLocaleString()}</strong></span>
                <span>Amrit Sarovar: <strong>{gpGrandTotals.amrit_sarovar_uploaded.toLocaleString()}</strong></span>
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

/* ================= CLICK STYLE ================= */
const clickStyle = {
  color: "#1677ff",
  cursor: "pointer",
  textDecoration: "underline",
  fontWeight: 500,
};

export default ReportAdmin;